import { TSourceDetail } from '@core/interfaces/TMessage';

import { Conversation } from '../../infrastructure/models/Conversation';
import { MessageAuthorV2, TAdaptedMessageV2, TAdaptedParagraphV2, TAdaptedQuizQuestion, TAdaptedReferenceV3 } from '../../types/types';
import { expandListParagraphs } from '../utils/list-item-splitter';
import { buildAssistantParagraphsLikeStreaming } from '../utils/streaming-paragraph-builder';

import { HistoryConversationDto, QuizReferenceDto, SearchedSourcesData } from '../../../api/dtos/conversation.dto';
import { IConversationAdapter } from './adapter.interface';
import { ReferenceTransformer } from './transformers/reference-transformer';

export class HistoryAdapter implements IConversationAdapter<HistoryConversationDto> {
  constructor(private isBotUiConfigEnabled: boolean = false) {}
  /**
   * Maps the SearchedSourcesData format to TSourceDetail[] format
   * Creates one TSourceDetail per embedding (like old sourceDetails format)
   */
  private mapSearchedSourcesToSourceDetails(searchedSources: SearchedSourcesData): TSourceDetail[] {
    const sourceDetails: TSourceDetail[] = [];

    searchedSources.sources.forEach(source => {
      // If source has embeddings, create one TSourceDetail per embedding
      if (source.embeddings && source.embeddings.length > 0) {
        source.embeddings.forEach(embedding => {
          const ext = embedding.externalReferences;
          sourceDetails.push({
            _id: source.sourceId,
            name: source.sourceName,
            references: source.fullContent, // Full document content for preview
            citation: source.references, // Citation string (title, authors, date) for display name
            folder: source.folderInfo ? [source.folderInfo.folderName] : [],
            embeddingContent: embedding.content,
            embeddingID: embedding.embeddingId,
            fileLink: undefined,
            embeddingExternalReferences:
              ext && ext.length > 0 ? ext.map(r => ({ ...r })) : undefined,
            uiExternalRefsToDisplay: ext?.map(r => r.referenceText),
          });
        });
      } else {
        // Fallback: create one TSourceDetail without embedding info
        sourceDetails.push({
          _id: source.sourceId,
          name: source.sourceName,
          references: source.fullContent, // Full document content for preview
          citation: source.references, // Citation string (title, authors, date) for display name
          folder: source.folderInfo ? [source.folderInfo.folderName] : [],
          embeddingContent: undefined,
          embeddingID: undefined,
          fileLink: undefined,
          uiExternalRefsToDisplay: undefined,
        });
      }
    });

    return sourceDetails;
  }

  public adapt(dataDto: HistoryConversationDto, currentConversation: Conversation): Conversation {
    currentConversation.id = dataDto._id;
    currentConversation.embeddingContent = dataDto.embeddingContent;
    currentConversation.content = dataDto.content;
    currentConversation.favorite = dataDto.favorite; // Add conversation-level favorite mapping

    if (this.isQuizConversation(dataDto)) {
      this.adaptQuestions(dataDto, currentConversation);
      return currentConversation;
    }

    // STEP 1: First pass - accumulate all sources before processing references
    let allSourcesForReferences: TSourceDetail[] | undefined;

    // --- ROLLBACK (sources grid): To revert to "first message with sources only", inside the forEach
    // add back: if (!currentConversation.originalSourceDetails && mappedSources.length > 0) {
    //   currentConversation.originalSourceDetails = mappedSources;
    // }
    // and remove the block below that sets originalSourceDetails from allSourcesForReferences.
    dataDto.messages.forEach(msgDto => {
      // Only accumulate sources, don't create messages yet
      if (msgDto.searchedSources) {
        const mappedSources = this.mapSearchedSourcesToSourceDetails(msgDto.searchedSources);

        // Accumulate ALL sources for reference lookup (and for grid display below)
        if (allSourcesForReferences) {
          // Merge with existing sources, avoiding duplicates by sourceId + embeddingID
          const existingKeys = new Set(
            allSourcesForReferences.map(source => `${source._id}|${source.embeddingID || 'none'}`)
          );

          const newSources = mappedSources.filter(
            source => !existingKeys.has(`${source._id}|${source.embeddingID || 'none'}`)
          );

          allSourcesForReferences = [...allSourcesForReferences, ...newSources];
        } else {
          allSourcesForReferences = mappedSources;
        }
      }
      // Fallback to legacy sourceDetails format
      else if (msgDto.sourceDetails) {
        const sourceDetails = msgDto.sourceDetails;

        // Accumulate ALL sources for reference lookup
        if (allSourcesForReferences) {
          const existingKeys = new Set(
            allSourcesForReferences.map(
              source => `${source._id}|${(source as any).embeddingID || 'none'}`
            )
          );

          const newSources = sourceDetails.filter(
            source => !existingKeys.has(`${source._id}|${(source as any).embeddingID || 'none'}`)
          );

          allSourcesForReferences = [...allSourcesForReferences, ...newSources];
        } else {
          allSourcesForReferences = sourceDetails;
        }
      }
    });

    // Set originalSourceDetails from merged set of all messages (dedupe by _id).
    // ROLLBACK: Remove this block and set originalSourceDetails inside the forEach above from the
    // first message that has searchedSources (see ROLLBACK comment above).
    if (allSourcesForReferences && allSourcesForReferences.length > 0) {
      const seenIds = new Set<string>();
      currentConversation.originalSourceDetails = allSourcesForReferences.filter(s => {
        if (seenIds.has(s._id)) return false;
        seenIds.add(s._id);
        return true;
      });
    }

    // Store all accumulated sources for reference lookup (temporary property)
    (currentConversation as any).allSourcesForReferences = allSourcesForReferences;

    // STEP 2: Second pass - create messages and process references with full context
    dataDto.messages.forEach(msgDto => {
      const newMsg = currentConversation.addNewMessage(
        msgDto.type as MessageAuthorV2,
        msgDto.type === 'user' ? msgDto.text : undefined,
        msgDto.date,
        msgDto.keywords
      );
      newMsg._id = msgDto._id;
      newMsg.favorite = msgDto.favorite; // Add this line to preserve the favorite state

      // Map uploaded files to the files property that the UI expects
      if (msgDto.uploadedFiles && msgDto.uploadedFiles.length > 0) {
        newMsg.files = msgDto.uploadedFiles;
      }

      // Map reference files to the referenceFiles property
      if (msgDto.referenceFiles && msgDto.referenceFiles.length > 0) {
        newMsg.referenceFiles = msgDto.referenceFiles;
      }

      // Set individual message sources (accumulation already done in first pass)
      if (msgDto.searchedSources) {
        const mappedSources = this.mapSearchedSourcesToSourceDetails(msgDto.searchedSources);
        newMsg.additionalSourceDetails = mappedSources;
      }
      // Fallback to legacy sourceDetails format
      else if (msgDto.sourceDetails) {
        newMsg.additionalSourceDetails = msgDto.sourceDetails;
      }
      // For assistant messages without sources, don't inherit conversation sources
      // ReferenceTransformer can find them via conversation.originalSourceDetails fallback

      // Map content to additionalContent for assistant messages
      if (msgDto.type === 'assistant' && msgDto.content) {
        newMsg.additionalContent = msgDto.content;
      }

      if (msgDto.type === 'assistant' && msgDto.text) {
        // Always create paragraphs from the FULL message text so nothing is lost.
        // processedSegments only contain chunks that were sent for reference processing;
        // any content filtered out (headings, preamble, metadata) would be missing
        // if we only used processedSegments.
        // Same paragraph graph as live streaming (not splitSegmentIntoParagraphs) so
        // renumberSequentially matches inline [n] and the References panel.
        const baseParagraphs = buildAssistantParagraphsLikeStreaming(msgDto.text);
        newMsg.paragraphs = expandListParagraphs(baseParagraphs);

        if (msgDto.processedSegments && msgDto.processedSegments.length > 0) {
          this.applySegmentReferences(
            currentConversation,
            newMsg,
            msgDto.processedSegments
          );
        }
      } else if (!msgDto.processedSegments || msgDto.processedSegments.length === 0) {
        if (msgDto.type === 'assistant') {
          newMsg.paragraphs.push({ text: msgDto.text || '', references: [] });
        }
      }
    });

    // Renumber references in all assistant messages sequentially by paragraph order.
    // Stored processedSegments may have been saved when concurrent backend processing
    // was in effect, so their indices could be in arrival order rather than document order.
    // This ensures history/results view numbering always matches the streaming view.
    currentConversation.messages.forEach((msg: TAdaptedMessageV2) => {
      if (msg.author === 'assistant') {
        ReferenceTransformer.renumberSequentially(msg, currentConversation);

        // DEBUG: dump final history reference state for parity comparison
        console.group('[REF-PARITY] History final state');
        console.log('Paragraph count:', msg.paragraphs.length);
        msg.paragraphs.forEach((p, i) => {
          if (p.references?.length) {
            console.log(`  P${i} refs:`, p.references.map(r =>
              `[${r.listPositionIndex}] ${r.sourceId}|${r.referenceDto?.embeddingId ?? 'none'}`
            ));
          }
        });
        console.log('Panel order:', msg.referencesToDisplay?.map(g =>
          g.children.map(c => `[${c.listPositionIndex}] ${c.sourceId}`).join(', ')
        ));
        console.groupEnd();
      }
    });

    return currentConversation;
  }

  private isQuizConversation(dataDto: HistoryConversationDto): boolean {
    if ((dataDto.questions?.length ?? 0) > 0) {
      return true;
    }

    return (dataDto.messages ?? []).some(msg => {
      if (!msg?.text) {
        return false;
      }
      try {
        const parsed = JSON.parse(msg.text) as { type?: string };
        return parsed?.type === 'quiz';
      } catch {
        return false;
      }
    });
  }

  /**
   * Ensure historical quiz payloads always have adaptedAnswer + correctAnswered
   * so results mode can render answered questions.
   */
  private normalizeHistoricalQuizQuestion(quizData: TAdaptedQuizQuestion): TAdaptedQuizQuestion {
    const normalized: TAdaptedQuizQuestion = {
      ...quizData,
      disabled: true,
    };

    if (!normalized.adaptedAnswer) {
      normalized.adaptedAnswer = {
        correct: this.adaptHistoricalQuizReferences(
          normalized.good_answer_reply_references ?? [],
          normalized.good_answer_reply ?? ''
        ),
        incorrect: this.adaptHistoricalQuizReferences(
          normalized.wrong_answer_reply_references ?? [],
          normalized.wrong_answer_reply ?? ''
        ),
      };
    }

    if (normalized.correctAnswered === undefined) {
      const selected = normalized.answers?.find(answer => answer.selected);
      if (selected) {
        normalized.correctAnswered = !!selected.correct;
      }
    }

    return normalized;
  }

  private adaptHistoricalQuizReferences(
    references: QuizReferenceDto[],
    text: string
  ): { text: string; references: TAdaptedReferenceV3[] } {
    if (!references || !Array.isArray(references)) {
      return { text, references: [] };
    }

    return {
      text,
      references: references.map((ref, index) => ({
        sourceId: ref.sourceId,
        listPositionIndex: index + 1,
        referenceDto: {
          sourceId: ref.sourceId,
          embeddingId: ref.embeddingId,
          similarity: ref.similarity,
          reasoning: ref.reasoning,
        },
        uiReasoning: ref.reasoning,
      })),
    };
  }

  private adaptQuestions(dataDto: HistoryConversationDto, currentConversation: Conversation) {
    // First, extract source details from messages with searchedSources
    const allSourceDetails: TSourceDetail[] = [];

    // Look for searchedSources in messages (quiz init sends this)
    dataDto?.messages.forEach(originalMsg => {
      if (originalMsg.searchedSources) {
        const mappedSources = this.mapSearchedSourcesToSourceDetails(originalMsg.searchedSources);
        mappedSources.forEach(sourceDetail => {
          const exists = allSourceDetails.some(
            s => s._id === sourceDetail._id && s.embeddingID === sourceDetail.embeddingID
          );
          if (!exists) {
            allSourceDetails.push(sourceDetail);
          }
        });
      }
    });

    // Set source details for the conversation
    if (allSourceDetails.length > 0) {
      currentConversation.originalSourceDetails = allSourceDetails;
    }

    // Now create messages
    // Store processedSegments from user messages to transfer to next assistant message
    let pendingUserProcessedSegments: any[] | null = null;
    // Track if we've seen the first assistant message (init message to skip)
    let firstAssistantMessageSkipped = false;

    dataDto?.messages.forEach(originalMsg => {
      try {
        const textData = JSON.parse(originalMsg.text) as
          | { type: 'quiz'; data: TAdaptedQuizQuestion }
          | { type: 'user'; data: string }
          | { type: 'assistant'; data: string };

        if (textData.type === 'quiz') {
          const msg = currentConversation.addNewMessage(
            'assistant',
            '',
            originalMsg.date,
            originalMsg.keywords
          );
          msg._id = originalMsg._id;
          msg.type = 'quiz';
          const quizData = this.normalizeHistoricalQuizQuestion(textData.data as TAdaptedQuizQuestion);
          msg.paragraphs = [
            {
              text: '',
              quizQuestions: [quizData],
            },
          ];
          msg.favorite = originalMsg.favorite;
        }
        // Skip user/assistant JSON messages in quiz conversations
        // They're just feedback messages that are already part of quiz data
        return;
      } catch (e) {
        // Handle plain text messages (non-JSON)
        if (originalMsg.type === 'user') {
          // User message - always display with the original short text
          const userMsg = currentConversation.addNewMessage(
            'user',
            originalMsg.text,
            originalMsg.date,
            originalMsg.keywords
          );
          userMsg._id = originalMsg._id;
          userMsg.favorite = originalMsg.favorite;

          // Map uploaded files for user messages
          if (originalMsg.uploadedFiles && originalMsg.uploadedFiles.length > 0) {
            userMsg.files = originalMsg.uploadedFiles;
          }

          // Map reference files
          if (originalMsg.referenceFiles && originalMsg.referenceFiles.length > 0) {
            userMsg.referenceFiles = originalMsg.referenceFiles;
          }

          // If user message has processedSegments, store them for next assistant message
          if (originalMsg.processedSegments && originalMsg.processedSegments.length > 0) {
            pendingUserProcessedSegments = originalMsg.processedSegments;
          }
        } else if (originalMsg.type === 'assistant') {
          // Skip only the FIRST assistant message (init/info text before quiz questions)
          // when bot-ui-config is enabled. Show subsequent assistant messages (like feedback).
          if (this.isBotUiConfigEnabled && !firstAssistantMessageSkipped) {
            // Skip only the first assistant init message
            firstAssistantMessageSkipped = true;
            pendingUserProcessedSegments = null; // Clear any pending segments
            return;
          }

          const assistantMsg = currentConversation.addNewMessage(
            'assistant',
            undefined, // Don't create an initial empty paragraph
            originalMsg.date,
            originalMsg.keywords
          );
          assistantMsg._id = originalMsg._id;
          assistantMsg.favorite = originalMsg.favorite;

          // Map uploaded files for assistant messages
          if (originalMsg.uploadedFiles && originalMsg.uploadedFiles.length > 0) {
            assistantMsg.files = originalMsg.uploadedFiles;
          }

          // Map reference files
          if (originalMsg.referenceFiles && originalMsg.referenceFiles.length > 0) {
            assistantMsg.referenceFiles = originalMsg.referenceFiles;
          }

          // Use pending segments from user message if available, otherwise use own segments
          const segmentsToUse = pendingUserProcessedSegments || originalMsg.processedSegments;
          pendingUserProcessedSegments = null; // Clear after use

          // Always create paragraphs from full message text so nothing is lost
          const baseParas = buildAssistantParagraphsLikeStreaming(originalMsg.text || '');
          assistantMsg.paragraphs =
            baseParas.length > 0
              ? expandListParagraphs(baseParas)
              : expandListParagraphs([{ text: originalMsg.text || '', references: [] }]);

          if (segmentsToUse && segmentsToUse.length > 0) {
            this.applySegmentReferences(
              currentConversation,
              assistantMsg,
              segmentsToUse
            );
          }
        }
      }
    });

    // Re-number inline references sequentially AFTER creating messages
    this.renumberQuizInlineReferences(currentConversation);
  }

  private renumberQuizInlineReferences(conversation: Conversation): void {
    // Re-number init message references separately from quiz answer references

    // First pass: Re-number references in assistant init message
    conversation.messages.forEach(msg => {
      if (msg.author === 'assistant' && msg.paragraphs.length > 0) {
        // Build a map of unique references by sourceId+embeddingId
        const referenceMap = new Map<string, number>(); // key: sourceId|embeddingId, value: new index
        let nextIndex = 1;

        // Collect unique references from paragraphs in order, sorting within
        // each paragraph by stable key for deterministic numbering.
        msg.paragraphs.forEach(paragraph => {
          if (paragraph.references && paragraph.references.length > 0) {
            const sorted = [...paragraph.references].sort((a, b) => {
              const keyA = `${a.sourceId}|${a.referenceDto?.embeddingId || 'none'}`;
              const keyB = `${b.sourceId}|${b.referenceDto?.embeddingId || 'none'}`;
              return keyA.localeCompare(keyB);
            });
            sorted.forEach(ref => {
              const key = `${ref.sourceId}|${ref.referenceDto?.embeddingId || 'none'}`;
              if (!referenceMap.has(key)) {
                referenceMap.set(key, nextIndex++);
              }
            });
          }
        });

        // Now re-number paragraph references using the map
        msg.paragraphs.forEach(paragraph => {
          if (paragraph.references && paragraph.references.length > 0) {
            paragraph.references.forEach(ref => {
              const key = `${ref.sourceId}|${ref.referenceDto?.embeddingId || 'none'}`;
              ref.listPositionIndex = referenceMap.get(key)!;
            });
          }
        });

        // Re-number the referencesToDisplay list using the same map
        if (msg.referencesToDisplay && msg.referencesToDisplay.length > 0) {
          msg.referencesToDisplay.forEach(refGroup => {
            refGroup.children.forEach(child => {
              const key = `${child.sourceId}|${child.referenceDto?.embeddingId || 'none'}`;
              const newIndex = referenceMap.get(key);
              if (newIndex !== undefined) {
                child.listPositionIndex = newIndex;
              }
            });
          });
        }
      }
    });

    // Second pass: Re-number quiz answer references separately (start from 1)
    let quizRefIndex = 1;
    conversation.messages.forEach(msg => {
      if (msg.type === 'quiz' && msg.paragraphs[0]?.quizQuestions) {
        msg.paragraphs[0].quizQuestions.forEach((question: TAdaptedQuizQuestion) => {
          if (question.correctAnswered !== undefined && question.adaptedAnswer) {
            // Only re-number the references that are actually displayed
            const refsToRenumber = question.correctAnswered
              ? question.adaptedAnswer.correct?.references
              : question.adaptedAnswer.incorrect?.references;

            if (refsToRenumber) {
              refsToRenumber.forEach(ref => {
                ref.listPositionIndex = quizRefIndex++;
              });
            }
          }
        });
      }
    });
  }

  /**
   * Match processedSegments to existing paragraphs (created from msgDto.text)
   * and apply references to the correct paragraphs.
   *
   * processedSegments are chunks sent for reference processing. They may cover
   * one or more consecutive paragraphs. We walk through paragraphs sequentially,
   * matching each segment to the paragraph(s) it covers by text containment.
   */
  private applySegmentReferences(
    conversation: Conversation,
    message: { paragraphs: TAdaptedParagraphV2[]; [key: string]: any },
    processedSegments: any[]
  ) {
    let searchFrom = 0;

    processedSegments.forEach((segmentDto: any, segmentIndex: number) => {
      const segText = (segmentDto.segment as string || '').trim();
      if (!segText) return;

      const range = this.findParagraphRange(message.paragraphs, segText, searchFrom);

      if (range.startIdx >= 0) {
        const targetIdx = this.findBestParagraphInRange(
          message.paragraphs,
          range.startIdx,
          range.endIdx
        );

        ReferenceTransformer.transform(
          conversation,
          message as any,
          processedSegments,
          segmentIndex,
          targetIdx
        );

        searchFrom = range.endIdx + 1;
      }
    });
  }

  /**
   * Find which consecutive paragraphs a processedSegment covers.
   * Uses prefix+suffix matching to find the start paragraph,
   * then checks subsequent paragraphs for containment in the segment text.
   */
  private findParagraphRange(
    paragraphs: TAdaptedParagraphV2[],
    segmentText: string,
    searchFrom: number
  ): { startIdx: number; endIdx: number } {
    const segNorm = segmentText.replace(/\s+/g, ' ').trim();

    for (let i = searchFrom; i < paragraphs.length; i++) {
      const paraText = (paragraphs[i].text || '').trim();
      if (!paraText || paraText.length < 5) continue;

      const paraNorm = paraText.replace(/\s+/g, ' ').trim();

      if (!this.paragraphMatchesSegment(paraNorm, segNorm)) continue;

      let endIdx = i;
      for (let j = i + 1; j < paragraphs.length; j++) {
        const nextText = (paragraphs[j].text || '').trim();
        if (!nextText || nextText.length < 5) { endIdx = j; continue; }

        const nextNorm = nextText.replace(/\s+/g, ' ').trim();

        if (this.paragraphMatchesSegment(nextNorm, segNorm)) {
          endIdx = j;
        } else {
          break;
        }
      }

      return { startIdx: i, endIdx };
    }

    return { startIdx: -1, endIdx: -1 };
  }

  /**
   * Check if a normalized paragraph's content appears in a normalized segment
   * using both prefix (first 80 chars) and suffix (last 40 chars) matching
   * for more precise identification than prefix-only.
   */
  private paragraphMatchesSegment(paraNorm: string, segNorm: string): boolean {
    const prefixLen = Math.min(80, paraNorm.length);
    const prefix = paraNorm.substring(0, prefixLen);
    if (!segNorm.includes(prefix)) return false;

    if (paraNorm.length > 40) {
      const suffix = paraNorm.substring(paraNorm.length - 40);
      if (!segNorm.includes(suffix)) return false;
    }

    return true;
  }

  /**
   * Find the best paragraph index within a range to place references on.
   * Walks backwards from the end, skipping headings, separators, and very short text.
   * Mirrors the logic in StreamReferencesService.findBestParagraphForRefs
   * so that history and streaming views show references in the same place.
   */
  private findBestParagraphInRange(
    paragraphs: TAdaptedParagraphV2[],
    startIdx: number,
    endIdx: number
  ): number {
    for (let i = endIdx; i >= startIdx; i--) {
      const text = paragraphs[i]?.text?.trim();
      if (!text) continue;

      // Skip pure headings
      if (/^#{1,6}\s/.test(text) && !text.includes('\n')) continue;

      // Skip separators
      if (/^[-*_]{3,}$/.test(text)) continue;

      // Skip very short text (preamble/transitional)
      if (text.length < 50) continue;

      return i;
    }

    // Fallback to the last paragraph in range
    return endIdx;
  }
}
